/**
 * Pure helpers for the Incident drawer (Safety V1 parity, Phase 2 Task 2.10).
 *
 * Child rows (parties / equipment / claims / costs) reuse the ticket child-row helpers
 * (`withKeys`, `childRowsForSave`, `childrenDirty`, `newChildRow`). The drug / alcohol
 * timing rules are V1 `chkdatestreason` (funcs_safety.pl 8226): a drug test completed more
 * than 32h after the incident, or an alcohol test more than 8h after, is a NO SHOW and
 * needs a reason; an alcohol test between 2h and 8h is LATE and needs a reason.
 */
import type { ClaimTypeLookup, IncidentsView } from '../../../types/safetySystem';
import { ChildRow, newChildRow } from './ticketForm';

export type { ClaimTypeLookup };

export type IncidentTabId = 'incident' | 'parties' | 'equipment' | 'tickets' | 'claims' | 'costs' | 'documents' | 'notes' | 'history';

/** Columns the drawer may post on `body.incident` (incidents.py INCIDENT_FIELDS, less `deleted` which Delete posts on its own). */
export const INCIDENT_FIELDS = [
  'gwiclaimnum', 'pronumber', 'incidentclosedate', 'incidentdatetime', 'incidentreportedby', 'incidenttype',
  'incidentrootcause', 'incidentdept', 'policenotified', 'incidentreportnum', 'officernum', 'officercounty',
  'officerprecinct', 'incidentloc', 'crossstreet', 'incidentadd', 'incidentcity', 'incidentstate', 'incidentzip',
  'incidentdescription', 'dotrecordable', 'datestrequired', 'drugtesttaken', 'drugtestcompletedatetime',
  'drugnoshowreason', 'alcoholtesttaken', 'alcoholtestcompletedatetime', 'alcoholnoshowreason', 'alcohollatereason',
  'statementrcvd', 'preventable', 'isbobtail', 'loadempty', 'ofirecno', 'accountability_group', 'totalclaimreserves',
] as const;

export const PARTY_FIELDS = [
  'partytype', 'driverrecno', 'unitnumber', 'invpartyname', 'invlicensenum', 'invlicensestate', 'invphonenum', 'invadd', 'invcity',
  'invstate', 'invzip', 'invcitation', 'invinjured', 'invambulance', 'invhospital', 'invfatality', 'invinjurydesc',
] as const;

export const EQUIPMENT_FIELDS = [
  'equiptype', 'equipowner', 'equipownername', 'unitnumber', 'ownerrecno', 'truckrecno', 'pronumber', 'ownernotifieddate',
  'equipdescription', 'hazmat', 'cominvrcvddate', 'insurance', 'insurancepolicynum', 'equipvin', 'equipphone', 'equipdamaged',
  'equiptowed', 'towingloc', 'equiptotaled', 'damagesdesc',
] as const;

export const CLAIM_FIELDS = [
  'claimnumber', 'claimtype', 'inhouse', 'claimadjuster', 'driverrecno', 'policyholder', 'policynum', 'claimemail', 'claimphone',
  'claimchecknum', 'claimamount', 'chargebacknum', 'claimcomments', 'claimsubmitdate', 'claimclosedate', 'claimgwideductible',
  'claimdriverdeductible', 'claimpolicytype', 'claimdriverpaid',
] as const;

export const COST_FIELDS = ['costtype', 'costdesc', 'paidtorecno', 'invoice', 'totalaccost', 'paymenttype', 'checknum', 'acpaiddate'] as const;

export const PARTY_TYPES = ['DRIVER', 'GWI DRIVER', 'GWI EMPLOYEE', 'GWI REPRESENTATIVE', 'WITNESS', 'OTHER', 'OWNER', 'PASSENGER'];
export const GWI_USER_PARTY_TYPES = new Set(['GWI EMPLOYEE', 'GWI REPRESENTATIVE']);
export const EQUIP_TYPES = ['GWI TRUCK', 'VEHICLE', 'OTHER', 'CONT/VAN/TRAILER', 'CHASSIS', 'CARGO', 'PROPERTY'];
export const LOAD_EMPTY_OPTIONS = [{ value: 'L', label: 'Loaded' }, { value: 'E', label: 'Empty' }];
export const PREVENTABLE_OPTIONS = [{ value: 'U', label: 'Undetermined' }, { value: 'N', label: 'No' }, { value: 'Y', label: 'Yes' }];

export const DRUG_NOSHOW_HOURS = 32;
export const ALCOHOL_NOSHOW_HOURS = 8;
export const ALCOHOL_LATE_HOURS = 2;

/** List view -> the drawer tab that owns the clicked row. */
export const VIEW_TAB: Record<IncidentsView, IncidentTabId> = {
  incident: 'incident', invparty: 'parties', equipcargo: 'equipment', claim: 'claims', cost: 'costs',
};

/** Row identity column per list view (incidents_query.py aliases). */
export const VIEW_IDENTITY: Record<IncidentsView, string> = {
  incident: 'incidentrecno', invparty: 'invrecno', equipcargo: 'ierecno', claim: 'claimrecno', cost: 'costrecno',
};

export const rowIdentity = (view: IncidentsView, row: Record<string, unknown>): string =>
  String(row[VIEW_IDENTITY[view]] ?? row.incidentrecno ?? '');

const ZERO_DATES = new Set(['', '0', '0000-00-00', '0000-00-00 00:00:00', 'None']);
const norm = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
export const dateSet = (v: unknown): boolean => !ZERO_DATES.has(norm(v).trim());
export const isYes = (v: unknown): boolean => norm(v).trim().toUpperCase() === 'Y';

export function pickIncidentFields(incident: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const f of INCIDENT_FIELDS) out[f] = incident[f] === null || incident[f] === undefined ? '' : incident[f];
  return out;
}

/** Pre-save state of a brand-new incident (mirrors incidents_save._new_incident_defaults + V1 form defaults). */
export function newIncidentDefaults(): Record<string, any> {
  const d = pickIncidentFields({});
  Object.assign(d, {
    pronumber: 0, incidenttype: 0, incidentrootcause: 0, incidentdept: 0, ofirecno: 0, accountability_group: 0, totalclaimreserves: 0,
    policenotified: 'N', dotrecordable: 'N', datestrequired: 'N', drugtesttaken: 'N', alcoholtesttaken: 'N', statementrcvd: 'N',
    isbobtail: 'N', preventable: 'U',
  });
  return d;
}

/**
 * The `incident` body of a CREATE: the form less every key still blank (`''` / null /
 * undefined / a zero date). The server's pre-save baseline already holds those blanks,
 * so posting them back only produced noise history rows (`'' -> 0000-00-00`); anything
 * the user actually typed (and the non-blank defaults such as `preventable: 'U'`) is kept.
 * Required fields left blank are still caught by the server's 422.
 */
export function createIncidentBody(incident: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(incident)) {
    if (v === null || v === undefined) continue;
    if (typeof v === 'string' && ZERO_DATES.has(v.trim())) continue;
    out[k] = v;
  }
  return out;
}

/** Parse a DB datetime ('YYYY-MM-DD HH:MM[:SS]' or ISO 'YYYY-MM-DDTHH:MM') as LOCAL time; null for blank / zero. */
export function parseDateTime(v: unknown): Date | null {
  const s = norm(v).trim();
  if (!dateSet(s)) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
  if (!m) return null;
  if (m[1] === '0000' || m[2] === '00' || m[3] === '00') return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0), Number(m[6] ?? 0));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Hours from `from` to `to` (fractional); null when either is blank / zero. */
export function hoursBetween(from: unknown, to: unknown): number | null {
  const a = parseDateTime(from);
  const b = parseDateTime(to);
  if (!a || !b) return null;
  return (b.getTime() - a.getTime()) / 3_600_000;
}

export function drugTestPrompt(incident: Record<string, any>): 'noshow' | null {
  const h = hoursBetween(incident.incidentdatetime, incident.drugtestcompletedatetime);
  return h !== null && h > DRUG_NOSHOW_HOURS ? 'noshow' : null;
}

export function alcoholTestPrompt(incident: Record<string, any>): 'noshow' | 'late' | null {
  const h = hoursBetween(incident.incidentdatetime, incident.alcoholtestcompletedatetime);
  if (h === null) return null;
  if (h > ALCOHOL_NOSHOW_HOURS) return 'noshow';
  if (h > ALCOHOL_LATE_HOURS) return 'late';
  return null;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** DB datetime -> `<input type="datetime-local">` value. */
export function toDateTimeInput(v: unknown): string {
  const d = parseDateTime(v);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `<input type="datetime-local">` value -> DB datetime ('' when cleared). */
export function fromDateTimeInput(v: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(norm(v).trim());
  if (!m) return '';
  return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}:${m[6] ?? '00'}`;
}

/** Claim-type pick: V1 claimtypechange fills the deductibles / policy / holder / type from the chain. */
export function applyClaimType<T extends ChildRow>(row: T, lookup: ClaimTypeLookup | null): T {
  if (!lookup) return { ...row, claimtype: 0 };
  return {
    ...row,
    claimtype: lookup.recno,
    claimgwideductible: norm(lookup.gwideductible),
    claimdriverdeductible: norm(lookup.driverdeductible),
    policynum: norm(lookup.policynum),
    policyholder: lookup.policyholderrecno ?? 0,
    claimpolicytype: lookup.policytyperecno ?? 0,
  };
}

export const costLinesFor = (lines: Record<string, any>[], acrecno: number): Record<string, any>[] =>
  lines.filter((l) => Number(l.acrecno) === Number(acrecno));

export const newPartyRow = (): ChildRow => newChildRow(PARTY_FIELDS, {
  partytype: 'GWI DRIVER', driverrecno: 0, invcitation: 'N', invinjured: 'N', invambulance: 'N', invhospital: 'N', invfatality: 'N',
});
export const newEquipmentRow = (): ChildRow => newChildRow(EQUIPMENT_FIELDS, {
  equiptype: 'GWI TRUCK', equipowner: 'GWI', ownerrecno: 0, truckrecno: 0, pronumber: 0, hazmat: 'N', equipdamaged: 'N', equiptowed: 'N',
  equiptotaled: 'N',
});
export const newClaimRow = (): ChildRow => newChildRow(CLAIM_FIELDS, {
  claimtype: 0, inhouse: 'N', driverrecno: 0, policyholder: 0, claimpolicytype: 0, claimdriverpaid: 'N',
});
export const newCostRow = (): ChildRow => newChildRow(COST_FIELDS, { costtype: 0, paidtorecno: 0, paymenttype: 0 });
export const newCostLine = (): ChildRow => newChildRow(['claimrecno', 'cost', 'glcode'], { claimrecno: 0 });

export const num = (v: unknown): number => {
  const n = Number(norm(v).replace(/[$,]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

export const money = (v: unknown): string => num(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
